/** Renders a forward-facing yellow explosion burst during dash attacks; shares the attack alpha lifecycle. */
import Phaser from 'phaser';
import { Component } from './Component';
import type { AttackState, AttackType } from './CombatComponent';
import type { Position } from '../utils/Types';
import { normalize } from '../utils/Math';
import { CRISP_SCALE } from '../ui/CrispUtil';

const STREAK_MAX = 100;
const CANVAS_SIZE = (STREAK_MAX + 16) * 2 * CRISP_SCALE + 4;
const WINDUP_ALPHA = 0.15;
const ACTIVE_ALPHA = 0.4;
const RECOVERY_FADE_MS = 100;
const INV_CRISP = 1 / CRISP_SCALE;
const TEX_KEY = '_dash-attack-burst';

/** Forward-facing yellow explosion overlay shown during any dash attack, independent of the skill's own arc. */
export class DashAttackVisualComponent implements Component {
  private scene: Phaser.Scene;
  private ownerSprite: Phaser.GameObjects.Sprite;
  private events: Phaser.Events.EventEmitter;
  private facingDirection: Position;

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private image: Phaser.GameObjects.Image;

  private currentTween: Phaser.Tweens.Tween | null = null;
  private currentState: AttackState = 'idle';
  private isDashAttack: boolean = false;

  /** Public so Phaser tweens can target it directly. */
  alpha: number = 0;

  constructor(
    scene: Phaser.Scene,
    ownerSprite: Phaser.GameObjects.Sprite,
    events: Phaser.Events.EventEmitter,
    facingDirection: Position
  ) {
    this.scene = scene;
    this.ownerSprite = ownerSprite;
    this.events = events;
    this.facingDirection = facingDirection;

    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_SIZE;
    this.canvas.height = CANVAS_SIZE;
    this.ctx = this.canvas.getContext('2d')!;

    if (!scene.textures.exists(TEX_KEY)) {
      scene.textures.addCanvas(TEX_KEY, this.canvas);
      scene.textures.get(TEX_KEY).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    this.image = scene.add.image(0, 0, TEX_KEY)
      .setScale(INV_CRISP)
      .setVisible(false);

    this.events.on('attack-state-changed', this.onAttackStateChanged, this);
  }

  private onAttackStateChanged(state: AttackState, _attackType: AttackType, isDashAttack = false): void {
    this.currentState = state;
    this.isDashAttack = isDashAttack;
    this.stopTween();

    switch (state) {
      case 'windup':
        this.alpha = WINDUP_ALPHA;
        break;

      case 'active':
        this.alpha = ACTIVE_ALPHA;
        break;

      case 'recovery':
        if (this.image.visible) {
          this.currentTween = this.scene.tweens.add({
            targets: this,
            alpha: 0,
            duration: RECOVERY_FADE_MS,
            onComplete: () => this.clearEffect(),
          });
        }
        break;

      case 'idle':
        this.clearEffect();
        break;
    }
  }

  private clearEffect(): void {
    this.stopTween();
    this.image.setVisible(false);
    this.alpha = 0;
    this.currentState = 'idle';
  }

  private stopTween(): void {
    if (this.currentTween) {
      this.currentTween.stop();
      this.currentTween = null;
    }
  }

  update(_delta: number): void {
    if (this.currentState === 'idle' || !this.isDashAttack) {
      this.image.setVisible(false);
      return;
    }

    const norm = normalize(this.facingDirection.x, this.facingDirection.y);
    this.drawBurst(norm.x, norm.y);
    (this.scene.textures.get(TEX_KEY) as Phaser.Textures.CanvasTexture).refresh();

    this.image.setPosition(this.ownerSprite.x, this.ownerSprite.y);
    this.image.setTint(0xffffff);
    this.image.setAlpha(this.alpha);
    this.image.setDepth(this.ownerSprite.y + 1.1);
    this.image.setVisible(true);
  }

  /** Draws 7 forward-facing yellow/orange streaks with gaussian blur falloff and white core spikes. */
  private drawBurst(normX: number, normY: number): void {
    const S = CRISP_SCALE;
    const center = CANVAS_SIZE / 2;
    const forwardAngle = Math.atan2(normY, normX);

    const streaks = [
      { angleOffset: -1.22, length: 45,  alphaMult: 0.45 }, // -70°
      { angleOffset: -0.70, length: 70,  alphaMult: 0.7  }, // -40°
      { angleOffset: -0.26, length: 90,  alphaMult: 0.9  }, // -15°
      { angleOffset:  0,    length: 100, alphaMult: 1.0  }, // center
      { angleOffset:  0.26, length: 90,  alphaMult: 0.9  }, // +15°
      { angleOffset:  0.70, length: 70,  alphaMult: 0.7  }, // +40°
      { angleOffset:  1.22, length: 45,  alphaMult: 0.45 }, // +70°
    ];

    const layers = [
      { lineWidth: 80, alphaScale: 0.03 },
      { lineWidth: 60, alphaScale: 0.05 },
      { lineWidth: 44, alphaScale: 0.08 },
      { lineWidth: 30, alphaScale: 0.12 },
      { lineWidth: 18, alphaScale: 0.2  },
      { lineWidth: 9,  alphaScale: 0.4  },
      { lineWidth: 3,  alphaScale: 0.75 },
    ];

    this.ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    this.ctx.lineCap = 'round';

    for (const streak of streaks) {
      const a = forwardAngle + streak.angleOffset;
      const tailX = center + Math.cos(a) * streak.length * S;
      const tailY = center + Math.sin(a) * streak.length * S;

      for (const layer of layers) {
        const alpha = streak.alphaMult * layer.alphaScale;
        const grad = this.ctx.createLinearGradient(center, center, tailX, tailY);
        grad.addColorStop(0,    `rgba(255, 255, 200, ${alpha})`);
        grad.addColorStop(0.25, `rgba(255, 230, 0,   ${alpha})`);
        grad.addColorStop(1,    `rgba(255, 120, 0,   0)`);

        this.ctx.strokeStyle = grad;
        this.ctx.lineWidth = layer.lineWidth * S;
        this.ctx.beginPath();
        this.ctx.moveTo(center, center);
        this.ctx.lineTo(tailX, tailY);
        this.ctx.stroke();
      }

      // White spike down the center of each streak
      const spikeGrad = this.ctx.createLinearGradient(center, center, tailX, tailY);
      spikeGrad.addColorStop(0,   `rgba(255, 255, 255, ${streak.alphaMult})`);
      spikeGrad.addColorStop(0.6, `rgba(255, 255, 255, ${streak.alphaMult * 0.4})`);
      spikeGrad.addColorStop(1,   `rgba(255, 255, 255, 0)`);
      this.ctx.strokeStyle = spikeGrad;
      this.ctx.lineWidth = 1.2 * S;
      this.ctx.beginPath();
      this.ctx.moveTo(center, center);
      this.ctx.lineTo(tailX, tailY);
      this.ctx.stroke();
    }
  }

  destroy(): void {
    this.events.off('attack-state-changed', this.onAttackStateChanged, this);
    this.stopTween();
    this.image.destroy();
    if (this.scene.textures.exists(TEX_KEY)) {
      this.scene.textures.remove(TEX_KEY);
    }
  }
}
